import { CanopyProvider } from '@bloomfieldrobotics/canopy';
import { GridLegacy as Grid, InputAdornment, Typography } from '@mui/material';
import { useMemo, useRef } from 'react';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useVariety } from '../../../services/entities/hooks/useVariety';
import {
  useAddBlockMutation,
  useDeleteBlockMutation,
  useGetBlocksQuery,
  useUpdateBlockMutation,
} from '../../../services/entities/impl/blfApi/endpoints/blocks';
import { useGetCustomersQuery } from '../../../services/entities/impl/blfApi/endpoints/customers';
import { useGetRanchesQuery } from '../../../services/entities/impl/blfApi/endpoints/ranches';
import { useGetAllScansQuery } from '../../../services/entities/impl/blfApi/endpoints/scans';
import { useGetUserMeQuery } from '../../../services/entities/impl/blfApi/endpoints/users';
import { UserRolesEnum } from '../../../services/entities/impl/blfApi/schemas/user';
import { CropTypes } from '../../../services/entities/impl/blfApi/schemas/variety';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import {
  OnboardFormStepEnum,
  selectCurrOnboardCustomerID,
  setCurrOnboardBlockID,
  setCurrOnboardCustomerID,
} from '../onboardingSlice';
import MapFormViewer from './blockBoundaryMapForm/MapFormViewer';
import { DeleteWarningBase } from './components/DeleteWarningBase';
import { VarietyFormDisplay } from './components/VarietyFormDisplay';
import { VarietySelectFormField } from './components/VarietySelectFormField';
import {
  FormController,
  GenericForm,
  GenericFormProps,
} from './generic/GenericForm';
import { ExtraValidationFunction } from './generic/context/CRUDContext';
import { FormFieldType } from './generic/crud/components/FormFieldType';
import { checkFieldUniqueness } from './generic/crud/utils/checkFieldUniqueness';
import { extractIDFromResponse } from './generic/crud/utils/extractIDFromResponse';
import { toSelectOptions } from './generic/crud/utils/toSelectOptions';
import { useGetCurrOnboardBlock } from './hooks/useGetCurrOnboardBlock';
import { BlockFormSchemaIn, BlockFormSchemaOut } from './schemas/block';

const useGetCurrBlockFormEntity = (): {
  currentData: yup.InferType<typeof BlockFormSchemaIn> | null;
  isLoading: boolean;
  isFetching: boolean;
} => {
  const {
    currentData: currOnboardBlock,
    isLoading,
    isFetching,
  } = useGetCurrOnboardBlock();
  const currOnboardCustomerID = useAppSelector(selectCurrOnboardCustomerID);

  // Get variety data for crop type
  const { variety, isVarietyLoading } = useVariety(currOnboardBlock?.varietyID);

  return {
    currentData: currOnboardBlock
      ? {
          ...currOnboardBlock,
          customerID: currOnboardCustomerID,
          cropType: variety?.cropType ?? ('UNKNOWN' as CropTypeEnum),
        }
      : null,
    isLoading: isLoading || isVarietyLoading,
    isFetching: isFetching || isVarietyLoading,
  };
};

export const BlockForm: React.FC = () => {
  const dispatch = useAppDispatch();

  const { currentData: customers } = useGetCustomersQuery();
  const { currentData: ranches } = useGetRanchesQuery();
  const { currentData: blocks } = useGetBlocksQuery();
  const { currentData: scans } = useGetAllScansQuery();
  const { currentData: authorizedUser } = useGetUserMeQuery();
  const [handleAdd] = useAddBlockMutation();
  const [handleUpdate] = useUpdateBlockMutation();
  const [handleDelete] = useDeleteBlockMutation();
  const { t } = useTranslation();
  const cropTypeOptions = toSelectOptions(CropTypes);
  const formControlsRef = useRef<FormController | null>(null);
  const currOnboardCustomerID = useAppSelector(selectCurrOnboardCustomerID);
  const { currentData: currEntity } = useGetCurrOnboardBlock();

  const customerOptions = useMemo(() => {
    // Reset selectedCustomerID if it's not in the customers list anymore
    if (
      customers &&
      !customers.some((customer) => customer.id === currOnboardCustomerID)
    ) {
      dispatch(setCurrOnboardCustomerID(''));
    }

    return customers?.map((c) => ({ value: c.id, label: c.name })) || [];
  }, [customers, currOnboardCustomerID, dispatch]);

  const ranchOptions = useMemo(() => {
    return (
      ranches
        ?.filter((ranch) => ranch.customerID === currOnboardCustomerID)
        .map((ranch) => ({ value: ranch.id, label: ranch.name })) || []
    );
  }, [ranches, currOnboardCustomerID]);

  /**
   * Handles changes in customer selection.
   * Updates the currently selected customer ID in the Redux state and resets the ranch ID field in the Formik form.
   *
   * @param value The new customer ID selected.
   */
  const handleCustomerChange = (value?: string) => {
    if (value) {
      dispatch(setCurrOnboardCustomerID(value));
      // Currently only handling this case for the Add form and skipping for the Edit form to avoid schema changes to the Block interface
      const addSetFieldValue =
        formControlsRef.current?.Add?.current?.setFieldValue;
      if (addSetFieldValue) {
        addSetFieldValue('ranchID', ''); // Reset ranchID in Formik's state
      }
    }
  };

  // Custom handleAdd function to remove customerID from the values object before sending it to the API since this is only used to filter ranches on the add form and is not part of the database schema
  const customHandleAdd = async (values: yup.AnyObject) => {
    const updatedValues = { ...values };
    delete updatedValues.customerID;
    return handleAdd(updatedValues);
  };

  // Custom handleUpdate function to remove cropType from the values
  // object before sending it to the API since this is only used to
  // filter the Variety field
  const customHandleUpdate = async (values: yup.AnyObject) => {
    const updatedValues = { ...values };
    delete updatedValues.cropType;
    return handleUpdate(updatedValues);
  };

  const deleteWarningProps = useMemo(() => {
    const warningText = t(
      'By deleting block all related scans will be deleted as well.',
      { ns: 'common' }
    );

    if (!currEntity || !scans) {
      return { warningText };
    }

    return {
      warningText,
      warnedChilds: {
        [t('Scans')]: {
          items: scans?.filter((scan) => scan.blockID === currEntity?.id),
        },
      },
    };
  }, [t, scans, currEntity]);

  const extraValidation: ExtraValidationFunction<
    yup.InferType<typeof BlockFormSchemaIn>
  > = (values) => {
    // Create a new object with all properties from values except customerID
    // Edge case for block creation, customerID doesn't exist in the schema but needed to filter ranches
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customerID, ...valuesFiltered } = values;

    return checkFieldUniqueness<yup.InferType<typeof BlockFormSchemaIn>>({
      dataset: blocks,
      fieldsToCheck: ['name'],
      values: valuesFiltered,
      currentItemId: currEntity?.id,
      datasetFilter: (values, el) => values.ranchID === el.ranchID,
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmitSuccess = async (res: any) => {
    const id = extractIDFromResponse(res);
    if (id) {
      dispatch(setCurrOnboardBlockID(id));
    }
  };

  const crudConfig: GenericFormProps<
    yup.InferType<typeof BlockFormSchemaIn>,
    yup.InferType<typeof BlockFormSchemaOut>
  > = {
    handleAdd: customHandleAdd,
    handleUpdate: customHandleUpdate,
    handleDelete,
    getCurrEntityHook: useGetCurrBlockFormEntity,
    extraValidation,
    onSubmitSuccess,
    yupSchemaIn: BlockFormSchemaIn,
    yupSchemaOut: BlockFormSchemaOut,
    slots: {
      [OnboardFormStepEnum.Add]: {
        props: {
          gridConfig: {
            numberOfColumns: 3,
          },
          hiddenFields: ['id'],
          fieldsConfig: {
            customerID: {
              type: FormFieldType.Select,
              label: t('Customer'),
              initialValue: '',
              config: {
                items: customerOptions,
                onChangeAction: handleCustomerChange,
              },
            },
            ranchID: {
              type: FormFieldType.Select,
              label: t('Ranch'),
              initialValue: '',
              config: {
                items: ranchOptions,
              },
            },
            name: {
              type: FormFieldType.TextField,
              label: t('Name'),
              initialValue: '',
            },
            cropType: {
              type: FormFieldType.Select,
              label: t('Crop Type'),
              initialValue: '',
              config: {
                items: cropTypeOptions,
              },
            },
            acres: {
              type: FormFieldType.TextField,
              label: t('Acres'),
              initialValue: null,
              config: {
                type: 'number',
              },
            },
            plantSpacing: {
              type: FormFieldType.TextField,
              label: t('Plant Spacing'),
              initialValue: null,
              config: {
                type: 'number',
                InputProps: {
                  endAdornment: (
                    <InputAdornment position='end'>ft</InputAdornment>
                  ),
                },
              },
            },
            rowSpacing: {
              type: FormFieldType.TextField,
              label: t('Row Spacing'),
              initialValue: null,
              config: {
                type: 'number',
                InputProps: {
                  endAdornment: (
                    <InputAdornment position='end'>ft</InputAdornment>
                  ),
                },
              },
            },
            trellisSystem: {
              type: FormFieldType.TextField,
              label: t('Trellis System'),
              initialValue: '',
            },
            pruningMethod: {
              type: FormFieldType.TextField,
              label: t('Pruning Method'),
              initialValue: '',
            },
            varietyID: {
              type: FormFieldType.Select,
              label: t('Variety'),
              initialValue: '',
              renderFn: (fieldProps) => {
                return <VarietySelectFormField {...fieldProps} />;
              },
            },
            rootStock: {
              type: FormFieldType.TextField,
              label: t('Root Stock'),
              initialValue: '',
            },
          },
        },
      },
      [OnboardFormStepEnum.Edit]: {
        renderAfter: () => <MapFormViewer showBlockBoundaryPopupButton />,
        props: {
          gridConfig: {
            numberOfColumns: 3,
          },
          hiddenFields: ['id'],
          fieldsConfig: {
            id: {
              // hidden by hiddenFields config prop but needed to set the edit id
              type: FormFieldType.TextField,
              label: 'ID',
            },
            ranchID: {
              type: FormFieldType.Select,
              label: t('Ranch'),
              initialValue: '',
              config: {
                items:
                  ranches?.map((c) => ({ value: c.id, label: c.name })) || [],
              },
            },
            name: {
              type: FormFieldType.TextField,
              label: t('Name'),
              initialValue: '',
            },
            cropType: {
              type: FormFieldType.Select,
              label: t('Crop Type'),
              initialValue: '',
              config: {
                items: cropTypeOptions,
              },
            },
            acres: {
              type: FormFieldType.TextField,
              label: t('Acres'),
              initialValue: '',
              config: {
                type: 'number',
              },
            },
            plantSpacing: {
              type: FormFieldType.TextField,
              label: t('Plant Spacing'),
              initialValue: '',
              config: {
                type: 'number',
                InputProps: {
                  endAdornment: (
                    <InputAdornment position='end'>{t('ft')}</InputAdornment>
                  ),
                },
              },
            },
            rowSpacing: {
              type: FormFieldType.TextField,
              label: t('Row Spacing'),
              initialValue: '',
              config: {
                type: 'number',
                InputProps: {
                  endAdornment: (
                    <InputAdornment position='end'>{t('ft')}</InputAdornment>
                  ),
                },
              },
            },
            trellisSystem: {
              type: FormFieldType.TextField,
              label: t('Trellis System'),
              initialValue: '',
            },
            pruningMethod: {
              type: FormFieldType.TextField,
              label: t('Pruning Method'),
              initialValue: '',
            },
            varietyID: {
              type: FormFieldType.Select,
              label: t('Variety'),
              renderFn: (fieldProps) => {
                return (
                  <VarietySelectFormField
                    {...fieldProps}
                    customerId={currOnboardCustomerID}
                  />
                );
              },
            },
            rootStock: {
              type: FormFieldType.TextField,
              label: t('Root Stock'),
              initialValue: '',
            },
          },
        },
      },
      [OnboardFormStepEnum.View]: {
        renderAfter: () => <MapFormViewer />,
        props: {
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            ranchID: {
              type: FormFieldType.TextDisplay,
              label: t('Ranch'),
              initialValue: '',
              config: {
                valueGetter: (field) =>
                  ranches?.find((c) => c.id === field.value)?.name ||
                  t('Ranch Not Found'),
              },
            },
            name: {
              type: FormFieldType.TextDisplay,
              label: t('Name'),
              initialValue: '',
            },
            cropType: {
              type: FormFieldType.TextDisplay,
              label: t('Crop Type'),
              initialValue: '',
              config: {
                valueGetter: (field) =>
                  cropTypeOptions.find((c) => c.value === field.value)?.label ||
                  t('Crop Type Not Found'),
              },
            },
            acres: {
              type: FormFieldType.TextDisplay,
              label: t('Acres'),
              initialValue: '',
            },
            plantSpacing: {
              type: FormFieldType.TextDisplay,
              label: t('Plant Spacing'),
              initialValue: '',
            },
            rowSpacing: {
              type: FormFieldType.TextDisplay,
              label: t('Row Spacing'),
              initialValue: '',
            },
            trellisSystem: {
              type: FormFieldType.TextDisplay,
              label: t('Trellis System'),
              initialValue: '',
            },
            pruningMethod: {
              type: FormFieldType.TextDisplay,
              label: t('Pruning Method'),
              initialValue: '',
            },
            varietyID: {
              type: FormFieldType.TextDisplay,
              label: t('Variety (Crop Type)'),
              initialValue: '',
              renderFn: (fieldProps) => {
                return (
                  <Grid container>
                    <Grid item xs={4}>
                      <Typography>{fieldProps.fieldMeta.label}</Typography>
                    </Grid>
                    <Grid item xs={7}>
                      <VarietyFormDisplay {...fieldProps} />
                    </Grid>
                  </Grid>
                );
              },
            },
            rootStock: {
              type: FormFieldType.TextDisplay,
              label: t('Root Stock'),
              initialValue: '',
            },
          },
        },
      },
      [OnboardFormStepEnum.Delete]: {
        renderAfter: () => <MapFormViewer />,
        props: {
          submitCondition: () =>
            !authorizedUser?.roles
              .map((role) => role.name)
              .includes(UserRolesEnum.root),
          WarningElement: <DeleteWarningBase {...deleteWarningProps} />,
          fieldsConfig: {
            id: {
              type: FormFieldType.TextDisplay,
              label: t('ID'),
              initialValue: '',
            },
            ranchID: {
              type: FormFieldType.TextDisplay,
              label: t('Ranch'),
              initialValue: '',
              config: {
                valueGetter: (field) =>
                  ranches?.find((c) => c.id === field.value)?.name ||
                  t('Ranch Not Found'),
              },
            },
            name: {
              type: FormFieldType.TextDisplay,
              label: t('Name'),
              initialValue: '',
            },
            cropType: {
              type: FormFieldType.TextDisplay,
              label: t('Crop Type'),
              initialValue: '',
              config: {
                valueGetter: (field) =>
                  cropTypeOptions.find((c) => c.value === field.value)?.label ||
                  t('Crop Type Not Found'),
              },
            },
            acres: {
              type: FormFieldType.TextDisplay,
              label: t('Acres'),
              initialValue: '',
            },
            plantSpacing: {
              type: FormFieldType.TextDisplay,
              label: t('Plant Spacing'),
              initialValue: '',
            },
            rowSpacing: {
              type: FormFieldType.TextDisplay,
              label: t('Row Spacing'),
              initialValue: '',
            },
            trellisSystem: {
              type: FormFieldType.TextDisplay,
              label: t('Trellis System'),
              initialValue: '',
            },
            pruningMethod: {
              type: FormFieldType.TextDisplay,
              label: t('Pruning Method'),
              initialValue: '',
            },
            varietyID: {
              type: FormFieldType.TextDisplay,
              label: t('Variety'),
              initialValue: '',
              renderFn: (fieldProps) => {
                return <VarietyFormDisplay {...fieldProps} />;
              },
            },
            rootStock: {
              type: FormFieldType.TextDisplay,
              label: t('Root Stock'),
              initialValue: '',
            },
          },
        },
      },
    },
  };
  return (
    <CanopyProvider>
      <GenericForm {...crudConfig} ref={formControlsRef} />
    </CanopyProvider>
  );
};
